import React, { ReactNode, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Menu, MenuItem, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { TopBarNewButton } from "./TopBarNewButton"

export type UserActionsMenuItemColor = "slate" | "teal" | "green" | "purple" | "amber" | "pink"

export type UserActionsMenuItemConfig = Readonly<{
    icon: IconDefinition
    color: UserActionsMenuItemColor
    title: ReactNode
    description?: ReactNode
    onClick: () => void
}>

type TopBarNewButtonMenuProps = Readonly<{
    items: UserActionsMenuItemConfig[]
}>

const useStyles = makeStyles()(() => ({
    menu: {
        maxWidth: "45rem",
        minWidth: "42rem",
        marginTop: ".5rem",
        borderRadius: ".5rem",
        boxShadow: "-1px 3px 10px 0 rgba(0, 0, 0, 0.14)",
        padding: "1.5rem 2rem",
    },
    container: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1.5rem",
    },
    menuItem: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: ".75rem",
        width: "100%",
    },
    iconBox: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
    },
    iconSlate: {
        color: tailwindPalette.slate[900],
        background: tailwindPalette.slate[200],
    },
    iconTeal: {
        color: tailwindPalette.teal[900],
        background: tailwindPalette.teal[200],
    },
    iconGreen: {
        color: tailwindPalette.green[900],
        background: tailwindPalette.green[200],
    },
    iconPurple: {
        color: tailwindPalette.purple[900],
        background: tailwindPalette.purple[200],
    },
    iconAmber: {
        color: tailwindPalette.amber[900],
        background: tailwindPalette.amber[200],
    },
    iconPink: {
        color: tailwindPalette.pink[900],
        background: tailwindPalette.pink[200],
    },
}))

export function TopBarNewButtonMenu({ items }: TopBarNewButtonMenuProps) {
    const { classes, cx } = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const open = Boolean(anchorEl)

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const getIconColorClass = (color: UserActionsMenuItemColor) => {
        switch (color) {
            case "slate":
                return classes.iconSlate
            case "teal":
                return classes.iconTeal
            case "green":
                return classes.iconGreen
            case "purple":
                return classes.iconPurple
            case "amber":
                return classes.iconAmber
            case "pink":
                return classes.iconPink
            default:
                return classes.iconSlate
        }
    }

    return (
        <>
            <TopBarNewButton onClick={handleMenuOpen} />

            <Menu
                slotProps={{ paper: { className: classes.menu } }}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={open}
                onClose={handleMenuClose}
            >
                <Box className={classes.container}>
                    {items.map((item, index) => (
                        <MenuItem
                            className={classes.menuItem}
                            onClick={() => {
                                handleMenuClose()
                                item.onClick()
                            }}
                            key={index}
                        >
                            <Box className={cx(classes.iconBox, getIconColorClass(item.color))}>
                                <FontAwesomeIcon icon={item.icon} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1">{item.title}</Typography>
                                {item.description && (
                                    <Typography variant="body2" color="textSecondary">
                                        {item.description}
                                    </Typography>
                                )}
                            </Box>
                        </MenuItem>
                    ))}
                </Box>
            </Menu>
        </>
    )
}
